import { Injectable, inject } from "@angular/core";
import { MessageService } from "primeng/api";

export interface ToastOptions {
  life?: number;
  closable?: boolean;
  sticky?: boolean;
}

@Injectable({
  providedIn: "root",
})
export class ToastService {
  private messageService = inject(MessageService);

  /**
   * Show success toast message
   */
  success(message: string, summary = "Success", options?: ToastOptions): void {
    this.messageService.add({
      severity: "success",
      summary,
      detail: message,
      life: options?.life || 5000,
      closable: options?.closable !== false,
      sticky: options?.sticky || false,
    });
  }

  /**
   * Show error toast message
   */
  error(message: string, summary = "Error", options?: ToastOptions): void {
    this.messageService.add({
      severity: "error",
      summary,
      detail: message,
      life: options?.life || 7000,
      closable: options?.closable !== false,
      sticky: options?.sticky || false,
    });
  }

  /**
   * Show warning toast message
   */
  warning(message: string, summary = "Warning", options?: ToastOptions): void {
    this.messageService.add({
      severity: "warn",
      summary,
      detail: message,
      life: options?.life || 5000,
      closable: options?.closable !== false,
      sticky: options?.sticky || false,
    });
  }

  /**
   * Show info toast message
   */
  info(message: string, summary = "Info", options?: ToastOptions): void {
    this.messageService.add({
      severity: "info",
      summary,
      detail: message,
      life: options?.life || 5000,
      closable: options?.closable !== false,
      sticky: options?.sticky || false,
    });
  }

  /**
   * Show error message based on error type with user-friendly messages
   */
  showError(
    error: unknown,
    defaultMessage = "Something went wrong. Please try again.",
  ): void {
    const message = this.getErrorMessage(error, defaultMessage);
    this.error(message);
  }

  /**
   * Convert technical errors to user-friendly messages
   */
  private getErrorMessage(error: unknown, defaultMessage: string): string {
    if (!error || typeof error !== "object") {
      return defaultMessage;
    }

    // Check for AWS Cognito specific errors
    if ("name" in error) {
      switch (error.name) {
        // Network errors
        case "NetworkError":
          return "Unable to connect to the server. Please check your internet connection and try again.";

        default:
          // Check if there's a message property with useful info
          if ("message" in error && typeof error.message === "string") {
            // Don't expose technical messages to users
            console.error("Technical error:", error.message);
          }
      }
    }

    // Check for network/fetch errors
    if ("code" in error) {
      switch (error.code) {
        case "NetworkError":
        case "NETWORK_ERROR":
          return "Unable to connect to the server. Please check your internet connection and try again.";
        case "TIMEOUT":
          return "The request took too long. Please try again.";
      }
    }

    // Default user-friendly message
    return defaultMessage;
  }

  /**
   * Clear all toast messages
   */
  clear(): void {
    this.messageService.clear();
  }
}
